import fetchBHApi from "@helper/fetchBHApi";
import { getGTMData, getMetaData } from "@helper/getMetaData";
import useShortFormVideo from "@hooks/useBrightcoveVideo/useShortFormVideo";
import type { BHBasePageProps } from "@routes/BHBasePage";
import BHBasePage from "@routes/BHBasePage";
import {
  fetchContext,
  ResponseType,
  TRouteWithRedirect,
} from "@sphtech/web2-core/ssr";
import { PageAdTargetingTypeEnum } from "@typings/Ads.d";
import type {
  BrightcovePlayer,
  LatestVideoResponse,
} from "@typings/Brightcove.d";
import { PlaylistType } from "@typings/Playlist.d";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { ClientLoaderFunctionArgs, useParams } from "react-router";

import LandscapeVideoPage from "./LandscapeVideoPage";
import VideoMetaData from "./VideoMetaData";

export function meta() {
  const sectionName = "videos";
  return [getMetaData(VideoMetaData(sectionName).metaTags)];
}

export default function LandscapeVideos() {
  const { level1: id } = useParams();
  const [offset, setOffset] = useState(0);
  const [videos, setVideos] = useState<LatestVideoResponse[]>();
  const [playing, setPlaying] = useState<LatestVideoResponse | undefined>();

  const shouldFullScreen = useRef<boolean>(false);

  const { videos: shortFormVideos } = useShortFormVideo({ limit: 4 });

  useEffect(() => {
    const fetchData = async () => {
      try {
        let video: LatestVideoResponse[] = [];
        if (id) {
          video = await fetchBHApi<LatestVideoResponse[]>(
            `latest-videos/${id}`,
            "GET",
          );
        }
        const videos = await fetchBHApi<LatestVideoResponse[]>(
          `latest-videos-playlist?playlistId=${PlaylistType.LONG_FORM}&size=4`,
          "GET",
        );
        setOffset(videos.length);

        const currentVideo = video.length > 0 ? video[0] : videos[0];

        setPlaying(currentVideo);
        currentVideo.nowPlaying = true;

        setVideos(
          videos.map((v) => ({ ...v, nowPlaying: v.id === currentVideo.id })),
        );
      } catch (error) {
        console.error("[Video page] Error fetching long form videos:", error);
      }
    };

    void fetchData();
  }, [id]);

  const containerRef = useRef<HTMLDivElement>(null);

  const handleLoadMoreVideo = async () => {
    const size = 4;
    const moreVideoApi = `latest-videos-playlist?playlistId=${PlaylistType.LONG_FORM}&size=${size}&offset=${offset}`;
    const moreVideos = await fetchBHApi<LatestVideoResponse[]>(
      moreVideoApi,
      "GET",
    );
    setVideos((prevVideos) => [...(prevVideos || []), ...moreVideos]);
    setOffset((prevSize) => prevSize + size);

    // This avoids GTM push.
    return 0;
  };

  const handleNowPlaying = useMemo(
    () => (id: string) => {
      setVideos(
        (prevValue) =>
          prevValue &&
          prevValue.map((video) => ({
            ...video,
            nowPlaying: video.id === id,
          })),
      );
    },
    [setVideos],
  );

  const handleOnClickThumbnail = useCallback(
    (video: LatestVideoResponse) => {
      const { id } = video;
      handleNowPlaying(id);
      setPlaying(video);
    },

    [handleNowPlaying, setPlaying],
  );

  const handleOnEndedVideo = useCallback(() => {
    if (!videos) return;

    const playlistLength = videos.length;
    if (playlistLength === 0) return;

    const currentIndex: number = videos.findIndex(
      (video) => video.id === playing?.id,
    );
    const nextIndex =
      currentIndex === -1 ? 0 : (currentIndex + 1) % playlistLength;

    handleNowPlaying(videos[nextIndex].id);
    setPlaying(videos[nextIndex]);
  }, [playing, videos, handleNowPlaying]);

  const handlePlayerReady = useCallback((playerInstance: BrightcovePlayer) => {
    playerInstance.on("fullscreenchange", () => {
      shouldFullScreen.current = !!playerInstance.isFullscreen?.();
    });

    playerInstance.on("loadstart", () => {
      if (shouldFullScreen.current !== playerInstance.isFullscreen?.()) {
        playerInstance.requestFullscreen?.();
      }
    });
  }, []);

  const sectionName = "videos";
  const pageAdTargetValue = PageAdTargetingTypeEnum.LISTING;
  const basePageProps: BHBasePageProps = {
    uniqueSectionName: sectionName,
    pageAdTargetValue: pageAdTargetValue,
    hideScrollableSectionMenu: true,
    hideNavigationInMobile: true,
  };

  const renderVideoPage = () => {
    return (
      <LandscapeVideoPage
        playing={playing}
        containerRef={containerRef}
        videos={videos}
        sectionName={sectionName}
        pageAdTargetValue={pageAdTargetValue}
        handleOnEndedVideo={handleOnEndedVideo}
        handleOnClickThumbnail={handleOnClickThumbnail}
        handleLoadMoreVideo={handleLoadMoreVideo}
        shortFormVideos={shortFormVideos}
        handlePlayerReady={handlePlayerReady}
      />
    );
  };

  return <BHBasePage {...basePageProps}>{renderVideoPage()}</BHBasePage>;
}

export function loader(): Promise<TRouteWithRedirect<object | null, string>> {
  const sectionName = "videos";
  const { gaData } = VideoMetaData(sectionName);

  return Promise.resolve({
    type: ResponseType.SUCCESS,
    statusCode: 200,
    payload: null,
    gtm: getGTMData(gaData),
  });
}

export async function clientLoader(args: ClientLoaderFunctionArgs) {
  const result = (await fetchContext(args)) as { context?: unknown };

  if (!result.context) {
    console.error(
      "Error occurred during client-side routing, returning server Loader Data",
    );
    return await args.serverLoader();
  }

  const typedResult: unknown = result.context;

  // Check for valid payload field
  if (
    !typedResult ||
    typeof typedResult !== "object" ||
    !("payload" in typedResult) ||
    !Array.isArray(typedResult.payload)
  ) {
    console.error("Client Loader: fetchContext() returned an invalid payload");
  }
  return typedResult as Promise<
    TRouteWithRedirect<LatestVideoResponse | null, string>
  >;
}
